import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Package, Plus, Pencil, Trash2, Search, Loader2, Building2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useCompany } from "../context/CompanyContext";

export default function CataloguePage() {
  const { selectedId, selectedCompany } = useCompany();
  const cid = selectedId !== "all" ? selectedId : "";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [dialog, setDialog] = useState(null);

  const load = useCallback(async () => {
    if (!cid) { setItems([]); setLoading(false); return; }
    setLoading(true);
    try { const { data } = await api.get("/catalog", { params: { company_id: cid } }); setItems(data); }
    catch { setItems([]); }
    finally { setLoading(false); }
  }, [cid]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      if (dialog.id) await api.patch(`/catalog/${dialog.id}`, dialog);
      else await api.post("/catalog", { ...dialog, company_id: cid });
      setDialog(null); load(); toast.success("Article enregistré");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Supprimer cet article ?")) return; await api.delete(`/catalog/${id}`); load(); };

  const filtered = items.filter((i) => i.name.toLowerCase().includes(q.toLowerCase()));

  if (!cid) return (
    <div className="h-full flex items-center justify-center bg-background p-8" data-testid="catalogue-no-company">
      <div className="text-center max-w-sm"><Building2 size={44} strokeWidth={1} className="mx-auto mb-3 text-zinc-500" /><p className="text-zinc-400 text-sm">Sélectionnez une entreprise pour gérer son catalogue de produits et services.</p></div>
    </div>
  );

  return (
    <div className="h-full overflow-y-auto bg-background">
      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-8">
        <div className="flex items-center justify-between gap-3 mb-1">
          <div className="flex items-center gap-2.5"><Package size={22} className="text-klein" /><h1 className="font-cabinet font-black text-3xl tracking-tight">Catalogue</h1></div>
          <button data-testid="catalog-new" onClick={() => setDialog({ name: "", description: "", unit: "unité", unit_price: 0, vat_rate: 20, category: "", active: true, purchasable: false, sale_mode: "quote", interval: "month" })} className="flex items-center gap-1.5 h-10 px-4 rounded-lg bg-klein text-zinc-950 font-semibold text-sm hover:brightness-110"><Plus size={16} /> Nouvel article</button>
        </div>
        <p className="text-zinc-400 text-sm mb-6">Produits et services réutilisables dans vos devis et factures — {selectedCompany?.name}.</p>

        <div className="relative mb-4 max-w-xs">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input data-testid="catalog-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="w-full bg-card border border-white/10 rounded-lg pl-9 pr-3 h-10 text-sm outline-none focus:ring-2 focus:ring-klein/20" />
        </div>

        {loading ? <div className="py-16 flex justify-center text-zinc-400"><Loader2 className="animate-spin" /></div>
          : filtered.length === 0 ? <div className="py-16 text-center text-zinc-400"><Package size={42} strokeWidth={1} className="mx-auto mb-3" /><p className="text-sm">Aucun article. Ajoutez votre premier produit ou service.</p></div>
            : (
              <div className="rounded-2xl border border-white/10 bg-card overflow-hidden" data-testid="catalog-table">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-zinc-400 text-xs border-b border-white/10"><th className="px-4 py-3">Article</th><th className="px-4 py-3">Unité</th><th className="px-4 py-3 text-right">PU HT</th><th className="px-4 py-3 text-right">TVA</th><th className="px-4 py-3 text-right">PU TTC</th><th className="px-4 py-3"></th></tr></thead>
                  <tbody>
                    {filtered.map((i) => (
                      <tr key={i.id} data-testid={`catalog-row-${i.id}`} className="border-b border-white/[0.06] group">
                        <td className="px-4 py-3"><div className="font-medium flex items-center gap-2">{i.name}{!i.active && <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-500/20 text-zinc-400">inactif</span>}</div>{i.description && <div className="text-xs text-zinc-500 truncate max-w-md">{i.description}</div>}</td>
                        <td className="px-4 py-3 text-zinc-400">{i.unit}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{i.unit_price.toFixed(2)} €</td>
                        <td className="px-4 py-3 text-right tabular-nums text-zinc-400">{i.vat_rate}%</td>
                        <td className="px-4 py-3 text-right tabular-nums font-medium">{(i.unit_price * (1 + i.vat_rate / 100)).toFixed(2)} €</td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <button data-testid={`catalog-edit-${i.id}`} onClick={() => setDialog({ ...i })} className="p-1.5 rounded hover:bg-white/10 text-zinc-400 opacity-60 group-hover:opacity-100"><Pencil size={14} /></button>
                          <button data-testid={`catalog-del-${i.id}`} onClick={() => del(i.id)} className="p-1.5 rounded hover:bg-white/10 text-signal opacity-60 group-hover:opacity-100"><Trash2 size={14} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      </div>

      {dialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setDialog(null)}>
          <div className="bg-card border border-white/10 rounded-2xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="catalog-dialog">
            <h2 className="font-cabinet font-bold text-lg mb-4">{dialog.id ? "Modifier l'article" : "Nouvel article"}</h2>
            <div className="space-y-3">
              <Field label="Nom"><input data-testid="catalog-name" value={dialog.name} onChange={(e) => setDialog({ ...dialog, name: e.target.value })} className="inp" autoFocus /></Field>
              <Field label="Description"><textarea data-testid="catalog-desc" rows={2} value={dialog.description} onChange={(e) => setDialog({ ...dialog, description: e.target.value })} className="inp resize-none" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Unité"><input data-testid="catalog-unit" value={dialog.unit} onChange={(e) => setDialog({ ...dialog, unit: e.target.value })} placeholder="unité, heure, jour…" className="inp" /></Field>
                <Field label="Catégorie"><input data-testid="catalog-cat" value={dialog.category} onChange={(e) => setDialog({ ...dialog, category: e.target.value })} className="inp" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Prix unitaire HT (€)"><input data-testid="catalog-price" type="number" min="0" step="0.01" value={dialog.unit_price} onChange={(e) => setDialog({ ...dialog, unit_price: parseFloat(e.target.value || "0") })} className="inp" /></Field>
                <Field label="TVA (%)"><input data-testid="catalog-vat" type="number" min="0" step="0.1" value={dialog.vat_rate} onChange={(e) => setDialog({ ...dialog, vat_rate: parseFloat(e.target.value || "0") })} className="inp" /></Field>
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="catalog-active" checked={dialog.active} onChange={(e) => setDialog({ ...dialog, active: e.target.checked })} className="w-4 h-4 accent-klein" /> Actif (disponible dans les devis)</label>

              <div className="rounded-xl border border-white/10 bg-background p-3 space-y-3">
                <label className="flex items-center gap-2 text-sm font-medium"><input type="checkbox" data-testid="catalog-purchasable" checked={!!dialog.purchasable} onChange={(e) => setDialog({ ...dialog, purchasable: e.target.checked })} className="w-4 h-4 accent-klein" /> 🛍️ Vendable dans la boutique de l'espace client</label>
                {dialog.purchasable && (
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Mode de vente">
                      <select data-testid="catalog-salemode" value={dialog.sale_mode || "quote"} onChange={(e) => setDialog({ ...dialog, sale_mode: e.target.value })} className="inp">
                        <option value="quote">Demande de devis</option>
                        <option value="instant">Achat direct (Stripe)</option>
                        <option value="subscription">Abonnement (bientôt)</option>
                      </select>
                    </Field>
                    {dialog.sale_mode === "subscription" && (
                      <Field label="Récurrence">
                        <select data-testid="catalog-interval" value={dialog.interval || "month"} onChange={(e) => setDialog({ ...dialog, interval: e.target.value })} className="inp">
                          <option value="month">Mensuel</option>
                          <option value="year">Annuel</option>
                        </select>
                      </Field>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setDialog(null)} className="h-9 px-4 rounded-lg border border-white/10 text-sm">Annuler</button>
              <button data-testid="catalog-save" onClick={save} disabled={!dialog.name?.trim()} className="h-9 px-4 rounded-lg bg-klein text-zinc-950 font-semibold text-sm disabled:opacity-50">Enregistrer</button>
            </div>
          </div>
        </div>
      )}
      <style>{`.inp{width:100%;background:hsl(var(--background));border:1px solid hsl(var(--border));border-radius:8px;padding:8px 10px;font-size:14px;outline:none}.inp:focus{box-shadow:0 0 0 2px rgba(0,47,167,.2)}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="block text-[11px] font-medium text-zinc-400 mb-1">{label}</label>{children}</div>;
}
